import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import HistoryIcon from "@mui/icons-material/History";
import { Box, Stack, Typography } from "@mui/material";
import InternCard from "../components/InternCard";
import InternStatusBadge from "../components/InternStatusBadge";
import InternTable, { InternTableColumn } from "../components/InternTable";

interface ReviewRow {
  id: string;
  platform: string;
  appVersion: string;
  buildNumber: string;
  status: string;
  statusDisplay: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  rejectionReason: string;
}

interface Props {
  reviews: ReviewRow[];
  /** Render without outer card chrome */
  embedded?: boolean;
  /** Scrollable table body height when content overflows */
  tableMaxHeight?: number | string;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getStatusConfig = (
  status: string
): {
  status: "success" | "warning" | "error" | "info" | "default";
  label: string;
} => {
  switch (status) {
    case "ready_for_sale":
    case "approved":
      return { status: "success", label: "Approved" };
    case "in_review":
      return { status: "info", label: "In Review" };
    case "waiting_for_review":
      return { status: "warning", label: "Waiting" };
    case "pending_developer_release":
      return { status: "info", label: "Pending Release" };
    case "rejected":
      return { status: "error", label: "Rejected" };
    case "processing_for_app_store":
      return { status: "info", label: "Processing" };
    default:
      return { status: "default", label: status || "Unknown" };
  }
};

const columns: InternTableColumn<ReviewRow>[] = [
  {
    id: "platform",
    label: "Platform",
    width: 90,
    render: (row) => {
      const isIOS = row.platform === "ios";
      const Icon = isIOS ? AppleIcon : AndroidIcon;
      return (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Icon
            sx={{
              fontSize: 16,
              color: isIOS ? "#007AFF" : "#3DDC84",
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: "var(--intern-text-primary)",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {isIOS ? "iOS" : "Android"}
          </Typography>
        </Stack>
      );
    },
  },
  {
    id: "version",
    label: "Version",
    width: 110,
    render: (row) => (
      <Stack>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-primary)",
            fontSize: "0.8125rem",
            fontWeight: 600,
          }}
        >
          v{row.appVersion}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.6875rem",
          }}
        >
          Build {row.buildNumber}
        </Typography>
      </Stack>
    ),
  },
  {
    id: "status",
    label: "Status",
    width: 130,
    render: (row) => {
      const statusConfig = getStatusConfig(row.status);
      return (
        <InternStatusBadge
          status={statusConfig.status}
          label={statusConfig.label}
          size="small"
        />
      );
    },
  },
  {
    id: "submittedAt",
    label: "Submitted",
    width: 110,
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: "var(--intern-text-secondary)",
          fontSize: "0.75rem",
        }}
      >
        {formatDate(row.submittedAt)}
      </Typography>
    ),
  },
  {
    id: "reviewedAt",
    label: "Reviewed",
    width: 110,
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: "var(--intern-text-tertiary)",
          fontSize: "0.75rem",
        }}
      >
        {formatDate(row.reviewedAt)}
      </Typography>
    ),
  },
  {
    id: "rejectionReason",
    label: "Notes",
    render: (row) => (
      <Typography
        variant="body2"
        sx={{
          color: row.rejectionReason
            ? "var(--intern-status-error)"
            : "var(--intern-text-tertiary)",
          fontSize: "0.75rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 200,
        }}
        title={row.rejectionReason || undefined}
      >
        {row.rejectionReason || "—"}
      </Typography>
    ),
  },
];

const InternAppReviewHistoryTable = ({
  reviews,
  embedded = false,
  tableMaxHeight,
}: Props) => {
  const emptyState = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        height: tableMaxHeight ?? undefined,
        color: "var(--intern-text-tertiary)",
      }}
    >
      <HistoryIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
      <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
        No review submissions yet
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize: "0.6875rem", mt: 0.5, opacity: 0.7 }}
      >
        Store review status appears here after submit
      </Typography>
    </Box>
  );

  if (reviews.length === 0) {
    if (embedded) return emptyState;
    return (
      <InternCard title="App Review History" variant="compact" fullHeight>
        {emptyState}
      </InternCard>
    );
  }

  const table = (
    <InternTable
      columns={columns}
      data={reviews}
      keyExtractor={(row) => row.id}
      variant="compact"
      emptyMessage="No review submissions"
      stickyHeader={Boolean(tableMaxHeight)}
      maxHeight={tableMaxHeight}
      nested={embedded}
    />
  );

  if (embedded) return table;

  return (
    <InternCard
      title="App Review History"
      variant="compact"
      noPadding
      fullHeight
    >
      {table}
    </InternCard>
  );
};

export default InternAppReviewHistoryTable;
